import { useEffect, useState } from "react";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Flame, Trophy, Coins, Target, Bot, ListChecks, ArrowRight } from "lucide-react";

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between">
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-neutral-500">{label}</p>
        {Icon && <Icon size={18} className="text-orange-600" />}
      </div>
      <p className="mt-3 text-3xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [recs, setRecs] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
    if (user.role === "ALUMNO") api.get("/recommendations").then((r) => setRecs(r.data)).catch(() => {});
  }, []);

  if (!d) return <p className="text-neutral-500">Cargando panel…</p>;

  return (
    <div className="space-y-8 fade-up" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Hola, {user.name.split(" ")[0]} 👋</h1>
        <p className="text-neutral-500 mt-1">
          {user.role === "ALUMNO" ? "Sigue con tu formación. Dakota y tu profesor están a tu lado." :
           user.role === "PROFESOR" ? "Panel de profesor: tus alumnos, grupos y contenidos." :
           user.role === "FRANQUICIA" ? "Panel de franquicia: gestiona tu sede Natur." :
           "Panel matriz: control total de Natur Academy."}
        </p>
      </div>

      {user.role === "SUPER_ADMIN" && (
        <>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <Stat label="Alumnos activos" value={d.students_active} testid="stat-students-active" />
          <Stat label="Nuevos registros hoy" value={d.new_today} testid="stat-new-today" />
          <Stat label="Usuarios conectados" value={d.online_users} testid="stat-online" />
          <Stat label="Comentarios pendientes" value={d.pending_comments} testid="stat-pending-comments" />
          <Stat label="Entradas por publicar" value={d.scheduled_posts} testid="stat-scheduled" />
          <Stat label="Franquicias" value={d.franchises} testid="stat-franchises" />
          <Stat label="Profesores" value={d.teachers} testid="stat-teachers" />
          <Stat label="Consultas Dakota" value={d.dakota_queries} icon={Bot} testid="stat-queries" />
          <Stat label="Respuestas caché" value={d.cache_hits} testid="stat-cache" />
          <Stat label="Registros auditoría" value={d.audit_count} testid="stat-audit" />
          <div className={`rounded-xl p-6 border ${d.dakota_enabled ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`} data-testid="stat-dakota-status">
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-neutral-500">Dakota IA</p>
            <p className={`mt-3 text-xl font-semibold ${d.dakota_enabled ? "text-green-700" : "text-red-700"}`}>
              {d.dakota_enabled ? "Activa" : "Apagada"}
            </p>
          </div>
        </div>
        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6" data-testid="recent-activity">
          <h2 className="text-lg font-semibold mb-3">Actividad reciente</h2>
          <ul className="space-y-2 text-sm">
            {(d.recent_activity || []).map((a, i) => (
              <li key={i} className="flex justify-between border-b border-neutral-100 pb-1.5">
                <span><span className="text-orange-700 font-medium">{a.action}</span> · {a.user_email}</span>
                <span className="text-neutral-400 text-xs">{new Date(a.date).toLocaleString("es-ES")}</span>
              </li>
            ))}
          </ul>
        </div>
        </>
      )}

      {user.role === "FRANQUICIA" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Stat label="Alumnos" value={d.students} testid="stat-students" />
          <Stat label="Profesores" value={d.teachers} testid="stat-teachers" />
          <Stat label="Blogs propios" value={d.blogs} testid="stat-blogs" />
          <Stat label="Grupos de discusión" value={d.groups} testid="stat-groups" />
          <Stat label="Mensajes recibidos" value={d.messages} testid="stat-messages" />
        </div>
      )}

      {user.role === "PROFESOR" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Stat label="Mis alumnos" value={d.students} testid="stat-students" />
          <Stat label="Mis grupos" value={d.groups} testid="stat-groups" />
          <Stat label="Debates abiertos" value={d.open_groups} testid="stat-open-groups" />
          <Stat label="Créditos Dakota" value={d.credits} icon={Coins} testid="stat-credits" />
        </div>
      )}

      {user.role === "ALUMNO" && (
        <>
          {recs && (
            <Link to="/app/recomendados" data-testid="recommendations-widget"
                  className="block rounded-xl p-5 dakota-glow bg-orange-50 border border-orange-100 hover:bg-orange-100 transition-colors">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <Target className="text-orange-600" size={20} />
                  <div>
                    <p className="font-semibold text-sm">🎯 Recursos Recomendados para Mí</p>
                    <p className="text-xs text-neutral-500">
                      {recs.weaknesses.length > 0
                        ? `Necesitas reforzar: ${recs.weaknesses.slice(0, 2).map((w) => w.name).join(", ")}`
                        : "Haz tu primer test para recibir recomendaciones personalizadas"}
                      {recs.alerts.length > 0 && ` · ${recs.alerts.length} alerta(s)`}
                    </p>
                  </div>
                </div>
                <span className="flex items-center gap-1 text-sm font-medium text-orange-700">Ver todo <ArrowRight size={14} /></span>
              </div>
            </Link>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Créditos Dakota" value={d.credits} icon={Coins} testid="stat-credits" />
            <Stat label="Puntos" value={d.points} icon={Trophy} testid="stat-points" />
            <Stat label="Racha (días)" value={d.streak_days} icon={Flame} testid="stat-streak" />
            <Stat label="Nota media" value={`${d.avg_score}%`} icon={Target} testid="stat-avg" />
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Últimos tests</h2>
                <Link to="/app/tests" className="text-sm text-orange-700 hover:underline" data-testid="go-tests-link">Hacer un test →</Link>
              </div>
              {d.recent_attempts.length === 0 ? (
                <p className="text-sm text-neutral-500">Aún no has hecho ningún test. ¡Empieza hoy!</p>
              ) : (
                <ul className="space-y-2">
                  {d.recent_attempts.map((a) => (
                    <li key={a.id} className="flex justify-between items-center text-sm border-b border-neutral-100 pb-2">
                      <span>{a.topic_name}</span>
                      <span className={`font-semibold ${a.score >= 70 ? "text-green-600" : "text-red-600"}`}>{a.score}%</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Insignias</h2>
              <div className="flex flex-wrap gap-2">
                {d.badges_catalog.map((b) => (
                  <span key={b.key} data-testid={`badge-${b.key}`}
                        className={`text-xs font-medium px-3 py-1.5 rounded-full border ${
                          d.badges.includes(b.key) ? "bg-amber-100 border-amber-300 text-amber-800" : "bg-neutral-100 border-neutral-200 text-neutral-400"}`}>
                    {b.name}
                  </span>
                ))}
              </div>
              <Link to="/app/dakota" data-testid="go-dakota-link"
                    className="mt-6 flex items-center gap-3 rounded-xl p-4 dakota-glow bg-orange-50 hover:bg-orange-100 transition-colors">
                <Bot className="text-orange-600" />
                <div>
                  <p className="text-sm font-semibold">Pregunta a Dakota IA</p>
                  <p className="text-xs text-neutral-500">Tienes {d.credits} créditos disponibles</p>
                </div>
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
